import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import ContainerContent from '../../components/ContainerContent';
import { getUrl } from '../../components/getSubjectId';
import { BackBtn } from '../../components/shared/NavigationButtons';
import './styles/mapStyles.css';

// Кастомная иконка маркера с правильными размерами
const customIcon = new L.Icon({
  iconUrl: '/marker-icon.svg',
  iconSize: [32, 46],
  iconAnchor: [16, 46],
  popupAnchor: [0, -46],
});

interface MarkerDataItem {
  title: string;
  adres: string;
  phone: string;
  email: string;
  site: string;
  longitude: number;
  latitude: number;
}

const SubjectNameGender = () => {
  const { t } = useTranslation()
  return <span className={'linkSpan afade'} data-fade>{t('goBack')}</span>
}

export const Step2CardA: React.FC = () => {
  const { t, ready } = useTranslation();
  const [isMobile, setIsMobile] = useState(false);
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<L.Map | null>(null);
  const markersRef = useRef<L.Marker[]>([]);
  const tooltipsRef = useRef<HTMLDivElement[]>([]);

  // Парсим JSON с обработкой ошибок
  const parseJsonSafe = <T,>(key: string, fallback: T): T => {
    try {
      const value = t(key);
      // Если переводы не загружены, t() возвращает сам ключ
      if (value === key || !value.startsWith('[')) {
        return fallback;
      }
      return JSON.parse(value);
    } catch {
      return fallback;
    }
  };

  const markerData: MarkerDataItem[] = parseJsonSafe('themeInfo.step2.cardA.markerData', [])
  const title: string = t('themeInfo.step2.cardA.title')
  const list: string[] = parseJsonSafe('themeInfo.step2.cardA.list', [])

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.matchMedia('(max-width: 700px)').matches);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    document.body.classList.add('max-width-1200');
    return () => {
      document.body.classList.remove('max-width-1200');
    };
  }, []);

  // Инициализация карты
  useEffect(() => {
    if (!mapRef.current || markerData.length === 0) return;

    // Создаем массив координат маркеров
    const points: [number, number][] = markerData.map(marker => [marker.latitude, marker.longitude]);

    // Создаем границы (bounds) на основе точек
    const bounds = L.latLngBounds(points);

    // Создание карты с автоматическим подбором масштаба
    mapInstance.current = L.map(mapRef.current).fitBounds(bounds, {
      padding: [50, 50] // Добавляем отступы, чтобы маркеры не прилипали к краям
    });

    // Добавление тайлов OpenStreetMap
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png').addTo(mapInstance.current);

    // Создание маркеров
    markersRef.current = markerData.map((data, index) => {
      const marker = L.marker([data.latitude, data.longitude], { icon: customIcon }).addTo(mapInstance.current!);

      // Создание кастомного тултипа
      const tooltip = document.createElement('div');
      tooltip.className = 'custom-marker-tooltip';
      tooltip.innerHTML = `
        <div class="tooltip-title">${data.title}</div>
        <div class="tooltip-address">${data.adres}</div>
        <div class="tooltip-phone">${data.phone}</div>
        <div class="tooltip-email"><a href="mailto:${data.email}">${data.email}</a></div>
        <div class="tooltip-site"><a href="https://${data.site}">${data.site}</a></div>
      `;

      // Добавляем тултип в DOM
      mapRef.current!.appendChild(tooltip);
      tooltipsRef.current.push(tooltip);

      // Функция позиционирования тултипа
      const positionTooltip = () => {
        if (!mapInstance.current) return;
        const pos = mapInstance.current.latLngToContainerPoint([data.latitude, data.longitude]);
        tooltip.style.left = (pos.x - 125) + 'px';
        tooltip.style.top = (pos.y - 45) + 'px';
        tooltip.style.transform = 'translateY(-100%)';
      };

      // Скрытие тултипа при начале движения карты
      if (mapInstance.current) {
        mapInstance.current.on('movestart', () => tooltip.style.display = 'none');
      }

      // Позиционирование тултипа после окончания движения
      if (mapInstance.current) {
        mapInstance.current.on('moveend', positionTooltip);
      }

      // Отображение тултипа при наведении на маркер
      marker.on('mouseover', () => {
        positionTooltip();
        tooltip.style.display = 'block';
      });

      // Скрытие тултипа при уходе с маркера
      marker.on('mouseout', () => {
        setTimeout(() => {
          if (tooltip && !tooltip.matches(':hover')) {
            tooltip.style.display = 'none';
          }
        }, 50);
      });

      // Обработчики для тултипа
      tooltip.onmouseover = () => {
        if (tooltip) tooltip.style.display = 'block';
      };
      tooltip.onmouseout = () => {
        if (tooltip) tooltip.style.display = 'none';
      };

      return marker;
    });

    // Очистка при размонтировании
    return () => {
      if (mapInstance.current) {
        mapInstance.current.remove();
      }
      // Удаляем тултипы из DOM
      tooltipsRef.current.forEach(tooltip => {
        if (tooltip.parentElement) {
          tooltip.parentElement.removeChild(tooltip);
        }
      });
      tooltipsRef.current = [];
      markersRef.current = [];
    };
  }, [markerData]);

  // Не рендерим контент пока переводы не загружены
  if (!ready) {
    return (
      <ContainerContent>
        <div className={'paddDesc'}>
          <div className="block" data-animch data-rel>
            <div style={{ padding: '20px 0' }}>Loading...</div>
          </div>
        </div>
      </ContainerContent>
    );
  }

  return (
    <ContainerContent>
      <div className={'paddDesc'}>
        <div className="block" data-animch data-rel>
          <div className={'wrapTitleNav'}>
            <Link to={getUrl('theme-info/step2')} className={'mytitle'} style={{ width: 'auto' }}>
              <BackBtn></BackBtn>
              {!isMobile && <SubjectNameGender />}
            </Link>
          </div>
          <div style={{ padding: '20px 0' }}>
            <h1>{title}</h1>
            {list.map((item, index) => {
              const [boldPart, ...restParts] = item.split(':');
              return (
                <p key={index}>
                  <strong>{boldPart}:</strong>{restParts.join(':')}
                </p>
              );
            })}

            {/* Контейнер для карты */}
            <div className="map-container" ref={mapRef}></div>
          </div>
        </div>
      </div>
    </ContainerContent>
  );
};

export default Step2CardA;
